<div class="surface-card p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
        <div>
            <h2 class="text-text-primary text-xl font-semibold mb-1">{{ 'seekerDashboard.myContracts.title' | translate }}</h2>
            <p class="text-text-secondary text-sm">
                <ng-container *ngIf="!loading">
                    {{ contracts.length }} {{ contracts.length !== 1 ?
                    ('seekerDashboard.myContracts.subtitleTotalPlural' | translate) :
                    ('seekerDashboard.myContracts.subtitleTotal' | translate) }} {{
                    'seekerDashboard.myContracts.subtitleSuffix' | translate }}
                </ng-container>
            </p>
        </div>
        <div class="w-12 h-12 flex items-center justify-center rounded-xl bg-surface-elevated text-primary border border-border">
            <i class="fa-solid fa-file-contract text-xl"></i>
        </div>
    </div>

    <!-- Loading -->
    <div *ngIf="loading" class="rounded-xl border border-border bg-surface shadow-elevated overflow-hidden">
        <div class="border-b border-border-strong bg-surface-elevated px-4 py-4">
            <div class="flex items-center gap-3">
                <span class="loader"></span>
                <span class="text-sm font-medium text-text-secondary">{{ 'seekerDashboard.myContracts.title' | translate }}</span>
            </div>
        </div>
        <div class="divide-y divide-border-subtle">
            <div *ngFor="let row of [1, 2, 3]" class="grid gap-4 px-4 py-4 md:grid-cols-4">
                <div class="skeleton h-4"></div>
                <div class="skeleton h-4"></div>
                <div class="skeleton h-4"></div>
                <div class="skeleton h-6 rounded-full"></div>
            </div>
        </div>
    </div>

    <!-- Error -->
    <div *ngIf="error && !loading" class="alert alert-danger max-w-md mx-auto items-center justify-between text-center rounded-2xl shadow-elevated mb-4">
        {{ error }}
    </div>

    <!-- Empty -->
    <div *ngIf="!loading && !error && contracts.length === 0" class="text-center py-16">
        <div class="w-16 h-16 bg-surface-elevated rounded-full flex items-center justify-center mx-auto mb-4 border border-border">
            <i class="fa-solid fa-file-circle-xmark text-text-muted text-2xl"></i>
        </div>
        <p class="text-text-secondary text-sm">{{ 'seekerDashboard.myContracts.noContracts' | translate }}</p>
    </div>

    <!-- Contracts List -->
    <div *ngIf="!loading && !error && contracts.length > 0" class="space-y-6">
        <div *ngFor="let contract of contracts"
            class="surface-card p-5 md:p-6 transition-all duration-300 hover:shadow-glow hover:-translate-y-0.5 hover:border-primary/50"
            [ngClass]="contract.type === 'sale' ? 'border-l-4 border-l-success' : 'border-l-4 border-l-primary'">

            <!-- Top row: type label + status badge -->
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5 pb-4 border-b border-border-subtle">
                <div class="flex items-center gap-3.5">
                    <div class="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors bg-surface-elevated border border-border"
                        [ngClass]="contract.type === 'sale' ? 'text-success' : 'text-primary'">
                        <i class="fa-solid text-xl" [ngClass]="contract.type === 'sale' ? 'fa-file-invoice-dollar' : 'fa-file-signature'"></i>
                    </div>
                    <div>
                        <div class="flex items-center gap-2">
                            <h3 class="text-text-primary font-bold text-base leading-snug">
                                {{ typeTranslationKey(contract.type) | translate }}
                            </h3>
                            <span class="badge text-xs"
                                [ngClass]="contract.type === 'sale' ? 'badge-success' : 'badge-primary'">
                                {{ typeTranslationKey(contract.type) | translate }}
                            </span>
                        </div>
                        <p class="text-text-secondary text-xs mt-1 flex items-center gap-1.5 font-mono">
                            <i class="fa-solid fa-house-chimney text-text-muted"></i>
                            <span>{{ 'seekerDashboard.myContracts.unit' | translate }}:</span>
                            <ng-container *ngIf="getUnitObject(contract.unit) as unitObj; else unitIdOnly">
                                <span class="font-medium text-text-primary" dir="auto">
                                    {{ unitObj.unitNumber }} - {{ getUnitTitle(unitObj) }}
                                </span>
                            </ng-container>
                            <ng-template #unitIdOnly>
                                <span class="font-medium text-text-secondary" dir="auto">
                                    {{ contract.unit || ('seekerDashboard.myContracts.notAssigned' | translate) }}
                                </span>
                            </ng-template>
                        </p>
                    </div>
                </div>
                <div class="flex items-center gap-2">
                    <span class="badge text-xs font-semibold whitespace-nowrap capitalize shadow-none"
                        [ngClass]="statusClass(contract.status)">
                        {{ statusTranslationKey(contract.status) | translate }}
                    </span>
                </div>
            </div>

            <!-- Details grid for Rental contract -->
            <div *ngIf="contract.type === 'rental'" class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-5">
                <div class="p-3 bg-surface-elevated rounded-xl border border-border transition-colors hover:border-primary/50 group">
                    <div class="text-xs text-text-muted font-mono tracking-wider mb-1.5 flex items-center gap-1.5 uppercase">
                        <i class="fa-regular fa-calendar-check group-hover:text-primary transition-colors"></i>
                        {{ 'seekerDashboard.myContracts.startDate' | translate }}
                    </div>
                    <div class="text-text-primary font-bold text-sm">{{ formatDate(contract.startDate) }}</div>
                </div>
                <div class="p-3 bg-surface-elevated rounded-xl border border-border transition-colors hover:border-primary/50 group">
                    <div class="text-xs text-text-muted font-mono tracking-wider mb-1.5 flex items-center gap-1.5 uppercase">
                        <i class="fa-regular fa-calendar-xmark group-hover:text-primary transition-colors"></i>
                        {{ 'seekerDashboard.myContracts.endDate' | translate }}
                    </div>
                    <div class="text-text-primary font-bold text-sm">{{ formatDate(contract.endDate) }}</div>
                </div>
                <div class="p-3 bg-surface-elevated rounded-xl border border-border transition-colors hover:border-primary/50 group">
                    <div class="text-xs text-text-muted font-mono tracking-wider mb-1.5 flex items-center gap-1.5 uppercase">
                        <i class="fa-solid fa-money-bill-wave group-hover:text-primary transition-colors"></i>
                        {{ 'seekerDashboard.myContracts.rentAmount' | translate }}
                    </div>
                    <div class="text-primary font-extrabold text-sm shadow-text">
                        {{ contract.rentAmount | number }} 
                        <span class="text-xs font-semibold text-text-muted font-mono tracking-wider">
                            {{ 'seekerDashboard.myContracts.currency' | translate }} / {{ 'seekerDashboard.myContracts.month' | translate }}
                        </span>
                    </div>
                </div>
                <div class="p-3 bg-surface-elevated rounded-xl border border-border transition-colors hover:border-primary/50 group">
                    <div class="text-xs text-text-muted font-mono tracking-wider mb-1.5 flex items-center gap-1.5 uppercase">
                        <i class="fa-regular fa-clock group-hover:text-primary transition-colors"></i>
                        {{ 'seekerDashboard.myContracts.createdAt' | translate }}
                    </div>
                    <div class="text-text-primary text-sm font-medium">{{ formatDate(contract.createdAt) }}</div>
                </div>
            </div>

            <!-- Details grid for Sale contract -->
            <div *ngIf="contract.type === 'sale'" class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-5">
                <div class="p-3 bg-surface-elevated rounded-xl border border-border transition-colors hover:border-primary/50 group">
                    <div class="text-xs text-text-muted font-mono tracking-wider mb-1.5 flex items-center gap-1.5 uppercase">
                        <i class="fa-solid fa-tags group-hover:text-primary transition-colors"></i>
                        {{ 'seekerDashboard.myContracts.finalPrice' | translate }}
                    </div>
                    <div class="text-success font-extrabold text-sm shadow-text">
                        {{ contract.finalPrice | number }} 
                        <span class="text-xs font-semibold text-text-muted font-mono tracking-wider">
                            {{ 'seekerDashboard.myContracts.currency' | translate }}
                        </span>
                    </div>
                </div>
                <div class="p-3 bg-surface-elevated rounded-xl border border-border transition-colors hover:border-primary/50 group">
                    <div class="text-xs text-text-muted font-mono tracking-wider mb-1.5 flex items-center gap-1.5 uppercase">
                        <i class="fa-solid fa-credit-card group-hover:text-primary transition-colors"></i>
                        {{ 'seekerDashboard.myContracts.paymentMethod' | translate }}
                    </div>
                    <div class="text-text-primary font-bold text-sm flex items-center gap-1.5">
                        <i class="fa-solid text-xs text-text-muted" [ngClass]="contract.paymentMethod === 'cash' ? 'fa-wallet' : 'fa-calendar-days'"></i>
                        {{ paymentMethodTranslationKey(contract.paymentMethod) | translate }}
                    </div>
                </div>
                <div class="p-3 bg-surface-elevated rounded-xl border border-border transition-colors hover:border-primary/50 group">
                    <div class="text-xs text-text-muted font-mono tracking-wider mb-1.5 flex items-center gap-1.5 uppercase">
                        <i class="fa-solid fa-circle-dollar-to-slot group-hover:text-primary transition-colors"></i>
                        {{ 'seekerDashboard.myContracts.paymentSummary' | translate }}
                    </div>
                    <div class="text-xs font-semibold flex flex-col gap-0.5 font-mono">
                        <span class="text-success flex justify-between gap-2">
                            <span class="text-text-muted">{{ 'seekerDashboard.myContracts.paid' | translate }}:</span>
                            <span class="font-extrabold">{{ contract.paymentSummary?.totalPaid | number }}</span>
                        </span>
                        <span class="text-warning flex justify-between gap-2">
                            <span class="text-text-muted">{{ 'seekerDashboard.myContracts.remaining' | translate }}:</span>
                            <span class="font-extrabold">{{ contract.paymentSummary?.totalRemaining | number }}</span>
                        </span>
                    </div>
                </div>
                <div class="p-3 bg-surface-elevated rounded-xl border border-border transition-colors hover:border-primary/50 group">
                    <div class="text-xs text-text-muted font-mono tracking-wider mb-1.5 flex items-center gap-1.5 uppercase">
                        <i class="fa-regular fa-clock group-hover:text-primary transition-colors"></i>
                        {{ 'seekerDashboard.myContracts.createdAt' | translate }}
                    </div>
                    <div class="text-text-primary text-sm font-medium">{{ formatDate(contract.createdAt) }}</div>
                </div>
            </div>

            <!-- Footer: Contract ID & Document link -->
            <div class="flex items-center justify-between pt-3 border-t border-border-subtle text-xs">
                <span class="text-text-muted flex items-center gap-1 font-mono">
                    <i class="fa-solid fa-fingerprint"></i>
                    {{ 'seekerDashboard.myContracts.contractId' | translate }} 
                    <span class="text-text-secondary select-all">{{ contract._id }}</span>
                </span>
                
                <!-- Optional Document Download/View -->
                <a *ngIf="getContractDocumentUrl(contract) as documentUrl" 
                   [href]="documentUrl" target="_blank" rel="noopener noreferrer"
                   class="text-primary hover:text-primary-light font-semibold flex items-center gap-1 transition-colors group">
                    <i class="fa-solid fa-file-arrow-down group-hover:-translate-y-0.5 transition-transform"></i>
                    {{ 'seekerDashboard.myContracts.downloadDoc' | translate }}
                </a>
            </div>
        </div>
    </div>
</div>
